import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Send, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { RestoreDialog } from "./RestoreLane";

/**
 * LanePressure — the two moves Connexis can make on a desk that has gone quiet, on the case
 * record itself: chase them again, or take the case off them with a reason they are told.
 * Pulling a lane closes it; once every lane is closed the case offers "send it to a new desk".
 */
const LIVE = ["sent", "in_review"];

export function usePullReasons() {
  const [reasons, setReasons] = useState([]);
  useEffect(() => {
    api.get("/hub/pull-reasons").then((r) => setReasons(r.data.items || [])).catch(() => {});
  }, []);
  return reasons;
}

const daysSince = (iso) => {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return Number.isFinite(d) && d >= 0 ? d : null;
};

function PutBack({ leadId, route, caseLabel, onDone }) {
  const [open, setOpen] = useState(false);
  const lid = route.lender_id;
  return (
    <>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)} data-testid={`hub-lane-restore-${lid}`}>
        Put back with {route.withdrawn ? "them" : "this desk"}
      </button>
      <RestoreDialog open={open} onOpenChange={setOpen} onDone={onDone} testId={`hub-lane-restore-dialog-${lid}`}
                     items={[{ lead_id: leadId, lender_id: lid, case: caseLabel || "this case", desk: route.company_name }]} />
    </>
  );
}

export function LaneActions({ leadId, route, reasons = [], onDone, caseLabel }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const lid = route.lender_id;

  useEffect(() => { if (open) { setReason(reasons[0] || ""); setNote(""); } }, [open, reasons]);

  if (route.status === "declined") return <PutBack leadId={leadId} route={route} caseLabel={caseLabel} onDone={onDone} />;
  if (!LIVE.includes(route.status)) return <span style={{ color: "var(--mut)", fontSize: 12.5 }}>—</span>;

  const waited = daysSince(route.status_updated_at || route.sent_at);
  const chases = Number(route.nudge_count || 0);

  const chase = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hub/leads/${leadId}/routes/${lid}/chase`, {});
      toast.success(`${data.desk} chased — round ${data.level}`, {
        description: "Email, text and a push into their portal.",
      });
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't get that out to them"); }
    finally { setBusy(false); }
  };

  const pull = async () => {
    if (!reason) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/hub/leads/${leadId}/routes/${lid}/pull`, {
        reason, note: note.trim() || null,
      });
      toast.success(`${data.desk} is off the case`, {
        description: `They've been emailed and texted the reason. ${data.live_lanes} desk${data.live_lanes === 1 ? "" : "s"} still on it.`,
      });
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't go through"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-ghost btn-sm" onClick={chase} disabled={busy}
                data-testid={`hub-lane-chase-${lid}`}>
          {busy ? "Working…" : chases ? `Chase again · ${chases}×` : "Chase this desk"}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)} disabled={busy}
                style={{ color: "var(--s-off-tx, #B91C1C)" }}
                data-testid={`hub-lane-pull-${lid}`}>
          Take it off them
        </button>
      </div>
      {waited != null && (
        <span style={{ color: "var(--mut)", fontSize: 11.5 }} data-testid={`hub-lane-waiting-${lid}`}>
          {waited}d on their desk{chases ? ` · chased ${chases}×` : " · never chased"}
        </span>
      )}

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="hub-lane-pull-dialog">
          <DialogHeader>
            <DialogTitle className="text-[17px]">Take this case off {route.company_name}</DialogTitle>
            <DialogDescription className="text-[13px]">
              Their lane closes, the client&apos;s paperwork comes off their portal, and they get an
              email, a text and a note on their own case record saying exactly why.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5" data-testid="hub-lane-pull-reasons">
            {reasons.map((x) => (
              <label key={x}
                     className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 text-[13px] transition-colors ${
                       reason === x ? "border-[#0055FF] bg-[#0055FF]/[0.04] text-slate-900"
                                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                     data-testid={`hub-lane-pull-reason-${x.replace(/\s+/g, "-").toLowerCase()}`}>
                <input type="radio" name="pull-reason" checked={reason === x} onChange={() => setReason(x)}
                       className="size-4 accent-[#0055FF]" />
                <span>{x}</span>
              </label>
            ))}
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Anything else they should know (optional) — it goes in the same email."
                    className="min-h-[70px] resize-none" data-testid="hub-lane-pull-note" />

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}
                    className="rounded-full" data-testid="hub-lane-pull-cancel">Cancel</Button>
            <Button onClick={pull} disabled={busy || !reason}
                    className="rounded-full bg-[#DC2626] hover:bg-[#B91C1C]"
                    data-testid="hub-lane-pull-confirm">
              {busy ? <Loader2 className="size-4 animate-spin" />
                    : <><UserMinus className="mr-1.5 size-3.5" /> Take it off them</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const LaneChaseAll = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hub/lead-nudges/run");
      toast.success(data.chased
        ? `Chased ${data.chased} desk${data.chased === 1 ? "" : "s"}`
        : "Nothing to chase — every lane has moved or been chased inside the last 2 days");
      onDone?.();
    } catch { toast.error("That chase didn't run"); }
    finally { setBusy(false); }
  };
  return (
    <button className="btn btn-ghost btn-sm" onClick={run} disabled={busy} data-testid="hub-lane-chase-all">
      {busy ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3" />}
      <span style={{ marginLeft: 6 }}>Chase every quiet desk</span>
    </button>
  );
};
